/* ===== BSTRO Feature: Custom Subscription Widget (START) ===== */
/*
 * Notes:
 * - Powers a custom subscription selector using native Shopify selling plans.
 * - Runs only when [data-bstro-subscription-widget] is present.
 * - Keeps hidden selling_plan input synchronized with variant + plan selection.
 */
(() => {
	const WIDGET_SELECTOR = "[data-bstro-subscription-widget]";
	const OPTION_CARD_SELECTOR = "[data-bstro-option-card]";
	const VARIANT_INPUT_SELECTOR = "[name='id'], [data-product-select], [data-variant-input]";
	const HIDDEN_CLASS = "is-hidden";
	const TOP_HIDDEN_CLASS = "hide";
	const DISABLED_CLASS = "is-disabled";
	const SELECTED_CLASS = "is-selected";
	const NO_SUBSCRIPTIONS_CLASS = "is-no-subscriptions";
	const ON_SALE_CLASS = "on-sale";

	const formatMoney = (cents) => {
		const value = Number.parseInt(cents, 10);
		if (!Number.isFinite(value)) return "";

		if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === "function") {
			return window.theme.Currency.formatMoney(value, window.theme.settings.moneyFormat);
		}

		return `$${(value / 100).toFixed(2)}`;
	};

	const toPositiveNumber = (value) => {
		const normalized = value === null || value === undefined ? "" : String(value);
		const parsed = Number.parseFloat(normalized.replace(/,/g, "").trim());
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	};

	const parseJSON = (widgetEl, selector) => {
		const scriptEl = widgetEl.querySelector(selector);
		if (!scriptEl) return null;

		try {
			return JSON.parse(scriptEl.textContent || "null");
		} catch (error) {
			return null;
		}
	};

	const toInt = (value) => {
		const parsed = Number.parseInt(String(value ?? ""), 10);
		return Number.isFinite(parsed) ? parsed : null;
	};

	const getFormForWidget = (widgetEl) => {
		if (!widgetEl) return null;
		const directForm = widgetEl.closest("form");
		if (directForm) return directForm;

		const productSection = widgetEl.closest(".product-section");
		if (!productSection) return null;
		return productSection.querySelector(".product-single__form");
	};

	const getWidgetKey = (widgetEl) => {
		if (!widgetEl) return null;
		if (widgetEl.dataset.bstroWidgetKey) return widgetEl.dataset.bstroWidgetKey;
		const generatedKey = `${widgetEl.dataset.sectionId || "section"}-${widgetEl.dataset.productId || "product"}-${Math.floor(Math.random() * 1000000)}`;
		widgetEl.dataset.bstroWidgetKey = generatedKey;
		return generatedKey;
	};

	const getHiddenSellingPlanInput = (widgetEl) => {
		const key = getWidgetKey(widgetEl);
		if (!key) return null;
		const formEl = getFormForWidget(widgetEl);
		let hiddenSellingPlanInput = widgetEl.querySelector("[data-bstro-selling-plan-input]");

		if (!hiddenSellingPlanInput && formEl) {
			hiddenSellingPlanInput = formEl.querySelector(`[data-bstro-selling-plan-input][data-bstro-widget-key="${key}"]`);
		}
		if (!hiddenSellingPlanInput) return null;

		hiddenSellingPlanInput.dataset.bstroWidgetKey = key;
		if (!formEl || hiddenSellingPlanInput.form === formEl) return hiddenSellingPlanInput;

		formEl.appendChild(hiddenSellingPlanInput);
		return hiddenSellingPlanInput;
	};

	const getPlanIdFromAllocation = (allocation) => {
		if (!allocation) return null;
		return toInt(allocation.selling_plan_id || (allocation.selling_plan && allocation.selling_plan.id));
	};

	const enforceTooltipTypography = (rootEl) => {
		if (!rootEl) return;
		const paragraphs = rootEl.querySelectorAll("p");
		paragraphs.forEach((pEl, index) => {
			const isLast = index === paragraphs.length - 1;
			pEl.style.margin = `0 0 ${isLast ? "0" : "10px"} 0`;
			pEl.style.marginTop = "0";
			pEl.style.marginBottom = isLast ? "0" : "10px";
			pEl.style.fontSize = "14px";
			pEl.style.lineHeight = "19.6px";
		});
		rootEl.querySelectorAll("strong, b, .rte--strong").forEach((strongEl) => {
			strongEl.style.fontWeight = "700";
		});
	};

	const bindLearnMore = (widgetEl) => {
		if (!widgetEl) return;
		const subscribeCard = widgetEl.querySelector("[data-bstro-option-card='subscribe']");
		if (!subscribeCard) return;

		const learnMoreNodes = subscribeCard.querySelectorAll("[data-bstro-learn-more]");
		learnMoreNodes.forEach((learnMoreEl) => {
			if (learnMoreEl.dataset.bstroLearnMoreBound === "true") return;
			learnMoreEl.dataset.bstroLearnMoreBound = "true";

			const triggerEl = learnMoreEl.querySelector(".bstro-subscription-widget__learn-more-trigger");
			const tooltipEl = learnMoreEl.querySelector("[data-bstro-learn-more-tooltip]");
			if (!triggerEl || !tooltipEl) return;
			enforceTooltipTypography(tooltipEl);

			const updateTooltipLayout = () => {
				const cardRect = subscribeCard.getBoundingClientRect();
				const learnMoreRect = learnMoreEl.getBoundingClientRect();
				const triggerRect = triggerEl.getBoundingClientRect();
				const iconEl = triggerEl.querySelector(".bstro-subscription-widget__learn-more-icon");
				const iconRect = iconEl ? iconEl.getBoundingClientRect() : triggerRect;
				const horizontalPadding = 14;

				const tooltipWidth = Math.max(220, cardRect.width - horizontalPadding * 2);
				const tooltipLeft = horizontalPadding - (learnMoreRect.left - cardRect.left);
				tooltipEl.style.width = `${tooltipWidth}px`;
				tooltipEl.style.left = `${tooltipLeft}px`;

				const iconCenterInLearnMore = iconRect.left - learnMoreRect.left + iconRect.width / 2;
				const iconCenterInTooltip = iconCenterInLearnMore - tooltipLeft - 7;
				const arrowLeft = Math.max(14, Math.min(Math.max(14, tooltipWidth - 14), iconCenterInTooltip));
				tooltipEl.style.setProperty("--bstro-tooltip-arrow-left", `${arrowLeft}px`);
			};

			const openTooltip = () => {
				enforceTooltipTypography(tooltipEl);
				updateTooltipLayout();
				learnMoreEl.classList.add("is-open");
			};

			const closeTooltip = () => {
				learnMoreEl.classList.remove("is-open");
			};

			learnMoreEl.addEventListener("mouseenter", openTooltip);
			learnMoreEl.addEventListener("mouseleave", closeTooltip);
			learnMoreEl.addEventListener("focusin", openTooltip);
			learnMoreEl.addEventListener("focusout", closeTooltip);
			window.addEventListener("resize", updateTooltipLayout);
		});
	};

	const getTopPriceElements = (productSection) => {
		if (!productSection) return null;
		return {
			price: productSection.querySelector("[data-product-price]"),
			priceWrapper: productSection.querySelector("[data-product-price-wrap]"),
			comparePrice: productSection.querySelector("[data-compare-price]"),
			savePrice: productSection.querySelector("[data-save-price]"),
			pricePerServing: productSection.querySelector("[data-price-per-serving]"),
			comparePriceA11y: productSection.querySelector("[data-compare-price-a11y]"),
			priceA11y: productSection.querySelector("[data-a11y-price]"),
		};
	};

	const readVariantServings = (variant) => {
		if (!variant) return null;
		const directRaw =
			variant.number_of_servings !== undefined && variant.number_of_servings !== null
				? variant.number_of_servings
				: variant.numberOfServings !== undefined && variant.numberOfServings !== null
					? variant.numberOfServings
					: variant.servings;
		const directValue = toPositiveNumber(directRaw);
		if (Number.isFinite(directValue)) return directValue;

		let metafieldValue = null;
		if (variant.metafields && variant.metafields.custom) {
			const servingsField = variant.metafields.custom.number_of_servings;
			if (servingsField && typeof servingsField === "object" && "value" in servingsField) {
				metafieldValue = servingsField.value;
			} else {
				metafieldValue = servingsField;
			}
		}
		return toPositiveNumber(metafieldValue);
	};

	const getProductServings = (productSection) => {
		if (!productSection) return null;
		if (productSection._hlProductServings !== undefined) {
			return toPositiveNumber(productSection._hlProductServings);
		}

		const productServingsJsonEl = productSection.querySelector("[data-product-servings-json]");
		if (productServingsJsonEl) {
			try {
				productSection._hlProductServings = JSON.parse(productServingsJsonEl.textContent || "null");
			} catch (error) {
				productSection._hlProductServings = null;
			}
		} else {
			productSection._hlProductServings = null;
		}

		return toPositiveNumber(productSection._hlProductServings);
	};

	// Unit labels come from custom.serving_unit_name / custom.serving_unit_name_short,
	// checked on the variant first, then the product; each falls back to the other
	// before the "serving" / "srv" defaults.
	const getServingUnits = (productSection, variant) => {
		if (!productSection) return { long: "serving", short: "srv" };
		if (productSection._hlServingUnitData === undefined) {
			productSection._hlServingUnitData = {};
			const unitJsonEl = productSection.querySelector("[data-serving-unit-json]");
			if (unitJsonEl) {
				try {
					productSection._hlServingUnitData = JSON.parse(unitJsonEl.textContent || "{}") || {};
				} catch (error) {
					productSection._hlServingUnitData = {};
				}
			}
		}

		const data = productSection._hlServingUnitData;
		const clean = (value) => (typeof value === "string" ? value.trim() : "");
		const variantUnits = (variant && data.variants && data.variants[String(variant.id)]) || {};
		const productUnits = data.product || {};
		const variantLong = clean(variantUnits.long);
		const variantShort = clean(variantUnits.short);
		const productLong = clean(productUnits.long);
		const productShort = clean(productUnits.short);

		return {
			long: variantLong || productLong || variantShort || productShort || "serving",
			short: variantShort || productShort || variantLong || productLong || "srv",
		};
	};

	const getServingsCount = (productSection, variant) => {
		const fromVariant = readVariantServings(variant);
		if (Number.isFinite(fromVariant)) return fromVariant;
		if (!productSection) return null;

		if (variant && Number.isFinite(variant.id)) {
			if (!productSection._hlVariantServingsById) {
				const servingsJsonEl = productSection.querySelector("[data-variant-servings-json]");
				if (servingsJsonEl) {
					try {
						productSection._hlVariantServingsById = JSON.parse(servingsJsonEl.textContent || "{}");
					} catch (error) {
						productSection._hlVariantServingsById = {};
					}
				} else {
					productSection._hlVariantServingsById = {};
				}
			}

			const variantServings = toPositiveNumber(productSection._hlVariantServingsById[String(variant.id)]);
			if (Number.isFinite(variantServings)) return variantServings;
		}

		return getProductServings(productSection);
	};

	const isElementVisible = (el) => {
		if (!el || !(el instanceof Element)) return false;
		if (el.hidden) return false;
		const styles = window.getComputedStyle(el);
		if (styles.display === "none" || styles.visibility === "hidden" || styles.opacity === "0") return false;
		return el.getClientRects().length > 0;
	};

	const getSelectedQuantity = (productSection) => {
		if (!productSection) return 1;
		const quantityInputs = productSection.querySelectorAll("[name='quantity']");
		if (!quantityInputs.length) return 1;

		for (const inputEl of quantityInputs) {
			if (!isElementVisible(inputEl)) continue;
			const quantity = Number.parseInt(inputEl.value, 10);
			if (Number.isFinite(quantity) && quantity > 0) return quantity;
		}

		const fallbackQuantity = Number.parseInt(quantityInputs[0].value, 10);
		return Number.isFinite(fallbackQuantity) && fallbackQuantity > 0 ? fallbackQuantity : 1;
	};

	const renderTopPrice = (refs, currentPrice, comparePrice, servingsCount = null, selectedQuantity = 1, servingUnits = null) => {
		if (!refs || !refs.price || !Number.isFinite(currentPrice)) return;
		let shouldShowPricePerServing = false;
		let pricePerServingText = "";
		if (Number.isFinite(servingsCount) && servingsCount > 0) {
			const quantity = Number.isFinite(selectedQuantity) && selectedQuantity > 0 ? selectedQuantity : 1;
			const totalPriceCents = currentPrice * quantity;
			const totalServings = servingsCount * quantity;
			const perServingPrice = totalServings > 0 ? formatMoney(Math.round(totalPriceCents / totalServings)) : "";
			if (perServingPrice) {
				pricePerServingText = `(${perServingPrice} / ${(servingUnits && servingUnits.long) || "serving"})`;
				shouldShowPricePerServing = true;
			}
		}

		refs.price.innerHTML = formatMoney(currentPrice);
		refs.price.classList.toggle(ON_SALE_CLASS, Number.isFinite(comparePrice) && comparePrice > currentPrice);

		if (refs.comparePrice) {
			refs.comparePrice.innerHTML = Number.isFinite(comparePrice) && comparePrice > currentPrice ? formatMoney(comparePrice) : "";
		}
		if (refs.priceWrapper) {
			refs.priceWrapper.classList.toggle(TOP_HIDDEN_CLASS, !(Number.isFinite(comparePrice) && comparePrice > currentPrice));
		}
		if (refs.comparePriceA11y) {
			refs.comparePriceA11y.setAttribute("aria-hidden", Number.isFinite(comparePrice) && comparePrice > currentPrice ? "false" : "true");
		}
		if (refs.priceA11y) {
			refs.priceA11y.setAttribute("aria-hidden", Number.isFinite(comparePrice) && comparePrice > currentPrice ? "false" : "true");
		}

		if (refs.savePrice) {
			if (Number.isFinite(comparePrice) && comparePrice > currentPrice) {
				let savings = comparePrice - currentPrice;
				if (window.theme && window.theme.settings && window.theme.settings.saveType === "percent") {
					savings = `${Math.round((savings * 100) / comparePrice)}%`;
				} else {
					savings = formatMoney(savings);
				}

				if (window.theme && window.theme.strings && window.theme.strings.savePrice) {
					refs.savePrice.innerHTML = window.theme.strings.savePrice.replace("[saved_amount]", savings);
				} else {
					refs.savePrice.innerHTML = `Save ${savings}`;
				}
				refs.savePrice.classList.remove(TOP_HIDDEN_CLASS);
			} else {
				refs.savePrice.classList.add(TOP_HIDDEN_CLASS);
			}
		}

		if (refs.pricePerServing) {
			refs.pricePerServing.textContent = shouldShowPricePerServing ? pricePerServingText : "";
			refs.pricePerServing.classList.toggle(TOP_HIDDEN_CLASS, !shouldShowPricePerServing);
		}
	};

	const getWidgetState = (widgetEl) => {
		if (widgetEl._bstroSubscriptionState) return widgetEl._bstroSubscriptionState;

		const variants = parseJSON(widgetEl, "[data-bstro-variants-json]") || [];
		const sellingPlanGroups = parseJSON(widgetEl, "[data-bstro-selling-plan-groups-json]") || [];
		const variantsById = new Map();
		const plansById = new Map();

		variants.forEach((variant) => {
			const variantId = toInt(variant && variant.id);
			if (!variantId) return;
			variantsById.set(variantId, variant);
		});

		sellingPlanGroups.forEach((group) => {
			const plans = (group && group.selling_plans) || [];
			plans.forEach((plan) => {
				const planId = toInt(plan && plan.id);
				if (!planId) return;
				plansById.set(planId, plan);
			});
		});

		const state = {
			variantsById,
			plansById,
		};

		widgetEl._bstroSubscriptionState = state;
		return state;
	};

	const getPricePerServingText = (priceCents, servingsCount, selectedQuantity = 1, servingUnits = null) => {
		const priceValue = toInt(priceCents);
		if (!Number.isFinite(priceValue) || !Number.isFinite(servingsCount) || servingsCount <= 0) return "";
		const quantity = Number.isFinite(selectedQuantity) && selectedQuantity > 0 ? selectedQuantity : 1;
		const totalPriceCents = priceValue * quantity;
		const totalServings = servingsCount * quantity;
		if (!Number.isFinite(totalServings) || totalServings <= 0) return "";
		const perServingPrice = formatMoney(Math.round(totalPriceCents / totalServings));
		return perServingPrice ? `${perServingPrice} / ${(servingUnits && servingUnits.short) || "srv"}` : "";
	};

	const updatePriceBlock = (cardEl, compareCents, priceCents, servingsCount = null, selectedQuantity = 1, servingUnits = null) => {
		if (!cardEl) return;

		const compareEl = cardEl.querySelector("[data-bstro-compare-price]");
		const priceEl = cardEl.querySelector("[data-bstro-current-price]");
		const pricePerServingEl = cardEl.querySelector("[data-bstro-price-per-serving]");
		const savePillEl = cardEl.querySelector("[data-bstro-save-pill]");
		const compareValue = toInt(compareCents);
		const priceValue = toInt(priceCents);

		if (priceEl) {
			priceEl.textContent = Number.isFinite(priceValue) ? formatMoney(priceValue) : "";
		}

		if (compareEl) {
			const showCompare = Number.isFinite(compareValue) && Number.isFinite(priceValue) && compareValue > priceValue;
			compareEl.textContent = showCompare ? formatMoney(compareValue) : "";
			compareEl.classList.toggle(HIDDEN_CLASS, !showCompare);
		}

		if (savePillEl) {
			const savings = Number.isFinite(compareValue) && Number.isFinite(priceValue) ? compareValue - priceValue : 0;
			const showSavings = savings > 0;
			savePillEl.textContent = showSavings ? `Save ${formatMoney(savings)}` : "";
			savePillEl.classList.toggle(HIDDEN_CLASS, !showSavings);
		}

		if (pricePerServingEl) {
			const pricePerServingText = getPricePerServingText(priceValue, servingsCount, selectedQuantity, servingUnits);
			pricePerServingEl.textContent = pricePerServingText;
			pricePerServingEl.classList.toggle(HIDDEN_CLASS, !pricePerServingText);
		}
	};

	const getSelectedVariantId = (widgetEl) => {
		const formEl = getFormForWidget(widgetEl);
		if (!formEl) return toInt(widgetEl.dataset.initialVariantId);

		const variantInput = formEl.querySelector("input[name='id']");
		const variantSelect = formEl.querySelector("[data-product-select]");
		return toInt(variantInput && variantInput.value) || toInt(variantSelect && variantSelect.value) || toInt(widgetEl.dataset.initialVariantId);
	};

	const normalizePlanLabel = (rawLabel) => {
		const source = String(rawLabel || "").trim();
		if (!source) return "";

		let normalized = source;
		normalized = normalized.replace(/\s+subscription\s+with\s+.*$/i, "");
		normalized = normalized.replace(/\s+with\s+.*discount.*$/i, "");
		normalized = normalized.split(":")[0].trim();
		return normalized || source;
	};

	const populatePlanSelect = (widgetEl, allocations, plansById) => {
		const planSelect = widgetEl.querySelector("[data-bstro-plan-select]");
		if (!planSelect) return null;

		const previousValue = toInt(planSelect.value);
		planSelect.innerHTML = "";

		const uniquePlans = new Map();
		allocations.forEach((allocation) => {
			const planId = getPlanIdFromAllocation(allocation);
			if (!planId || uniquePlans.has(planId)) return;
			uniquePlans.set(planId, allocation);
		});

		uniquePlans.forEach((allocation, planId) => {
			const optionEl = document.createElement("option");
			const plan = plansById.get(planId);
			optionEl.value = String(planId);
			optionEl.textContent = normalizePlanLabel((plan && plan.name) || `Plan ${planId}`);
			planSelect.appendChild(optionEl);
		});

		if (!planSelect.options.length) return null;

		const hasPrevious = previousValue && Array.from(planSelect.options).some((optionEl) => toInt(optionEl.value) === previousValue);
		if (hasPrevious) {
			planSelect.value = String(previousValue);
		} else {
			planSelect.selectedIndex = 0;
		}

		return toInt(planSelect.value);
	};

	const setPurchaseMode = (widgetEl, mode) => {
		const oneTimeRadio = widgetEl.querySelector("[data-bstro-mode-radio][value='one-time']");
		const subscribeRadio = widgetEl.querySelector("[data-bstro-mode-radio][value='subscribe']");
		if (oneTimeRadio) oneTimeRadio.checked = mode === "one-time";
		if (subscribeRadio) subscribeRadio.checked = mode === "subscribe";
	};

	const refreshWidget = (widgetEl) => {
		if (!widgetEl) return;

		const state = getWidgetState(widgetEl);
		const productSection = widgetEl.closest(".product-section");
		const topPriceRefs = getTopPriceElements(productSection);
		const variantId = getSelectedVariantId(widgetEl);
		const variant = state.variantsById.get(variantId) || null;
		if (!variant) return;
		const servingsCount = getServingsCount(productSection, variant);
		const servingUnits = getServingUnits(productSection, variant);
		const selectedQuantity = getSelectedQuantity(productSection);

		const oneTimeCard = widgetEl.querySelector("[data-bstro-option-card='one-time']");
		const subscribeCard = widgetEl.querySelector("[data-bstro-option-card='subscribe']");
		const subscribeDetails = widgetEl.querySelector("[data-bstro-subscribe-details]");
		const subscribeRadio = widgetEl.querySelector("[data-bstro-mode-radio][value='subscribe']");
		const hiddenSellingPlanInput = getHiddenSellingPlanInput(widgetEl);
		const oneTimeChecked = widgetEl.querySelector("[data-bstro-mode-radio][value='one-time']:checked");

		const oneTimePrice = toInt(variant.price);
		const oneTimeCompare = toInt(variant.compare_at_price);
		updatePriceBlock(oneTimeCard, oneTimeCompare, oneTimePrice, servingsCount, selectedQuantity, servingUnits);

		const allocations = Array.isArray(variant.selling_plan_allocations) ? variant.selling_plan_allocations : [];
		const hasSubscriptions = allocations.length > 0;
		widgetEl.classList.toggle(NO_SUBSCRIPTIONS_CLASS, !hasSubscriptions);

		if (subscribeRadio) {
			subscribeRadio.disabled = !hasSubscriptions;
		}
		if (subscribeCard) {
			subscribeCard.classList.toggle(DISABLED_CLASS, !hasSubscriptions);
		}
		if (!hasSubscriptions && !oneTimeChecked) {
			setPurchaseMode(widgetEl, "one-time");
		}
		if (!hasSubscriptions) {
			if (hiddenSellingPlanInput) {
				hiddenSellingPlanInput.disabled = true;
				hiddenSellingPlanInput.value = "";
			}
			renderTopPrice(topPriceRefs, oneTimePrice, oneTimeCompare, servingsCount, selectedQuantity, servingUnits);
			return;
		}

		let selectedPlanId = null;
		if (hasSubscriptions) {
			selectedPlanId = populatePlanSelect(widgetEl, allocations, state.plansById);
		} else {
			const planSelect = widgetEl.querySelector("[data-bstro-plan-select]");
			if (planSelect) planSelect.innerHTML = "";
		}

		const subscribeChecked = hasSubscriptions && !!widgetEl.querySelector("[data-bstro-mode-radio][value='subscribe']:checked");
		if (subscribeDetails) {
			// Keep subscribe details expanded even when one-time is selected.
			subscribeDetails.classList.toggle(HIDDEN_CLASS, !hasSubscriptions);
		}

		const selectedAllocation = allocations.find((allocation) => getPlanIdFromAllocation(allocation) === selectedPlanId) || allocations[0] || null;
		const subscribePrice = toInt(selectedAllocation && selectedAllocation.price);
		const subscribeCompare = oneTimeCompare;
		updatePriceBlock(subscribeCard, subscribeCompare, subscribePrice, servingsCount, selectedQuantity, servingUnits);

		if (hiddenSellingPlanInput) {
			if (subscribeChecked && selectedAllocation) {
				const planId = getPlanIdFromAllocation(selectedAllocation);
				hiddenSellingPlanInput.disabled = !planId;
				hiddenSellingPlanInput.value = planId ? String(planId) : "";
			} else {
				hiddenSellingPlanInput.disabled = true;
				hiddenSellingPlanInput.value = "";
			}
		}

		const selectedMode = subscribeChecked ? "subscribe" : "one-time";
		widgetEl.querySelectorAll(OPTION_CARD_SELECTOR).forEach((cardEl) => {
			const mode = cardEl.getAttribute("data-bstro-option-card");
			cardEl.classList.toggle(SELECTED_CLASS, mode === selectedMode);
		});

		if (subscribeChecked && Number.isFinite(subscribePrice)) {
			renderTopPrice(topPriceRefs, subscribePrice, subscribeCompare, servingsCount, selectedQuantity, servingUnits);
		} else {
			renderTopPrice(topPriceRefs, oneTimePrice, oneTimeCompare, servingsCount, selectedQuantity, servingUnits);
		}
	};

	const bindWidget = (widgetEl) => {
		if (!widgetEl || widgetEl.dataset.bstroSubscriptionBound === "true") return;
		widgetEl.dataset.bstroSubscriptionBound = "true";
		bindLearnMore(widgetEl);

		const subscribeRadio = widgetEl.querySelector("[data-bstro-mode-radio][value='subscribe']");
		if (subscribeRadio && !subscribeRadio.disabled) {
			subscribeRadio.checked = true;
		}

		widgetEl.addEventListener("change", (event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;
			if (!target.matches("[data-bstro-mode-radio], [data-bstro-plan-select]")) return;
			refreshWidget(widgetEl);
		});

		refreshWidget(widgetEl);
	};

	const initWidgets = (root = document) => {
		const scope = root instanceof Element || root instanceof Document ? root : document;
		const widgets = scope.matches && scope.matches(WIDGET_SELECTOR) ? [scope] : scope.querySelectorAll(WIDGET_SELECTOR);
		widgets.forEach((widgetEl) => bindWidget(widgetEl));
	};

	const refreshWidgets = (root = document) => {
		const scope = root instanceof Element || root instanceof Document ? root : document;
		const widgets = scope.matches && scope.matches(WIDGET_SELECTOR) ? [scope] : scope.querySelectorAll(WIDGET_SELECTOR);
		widgets.forEach((widgetEl) => refreshWidget(widgetEl));
	};

	const scheduleRefresh = (root = document) => {
		refreshWidgets(root);
		window.setTimeout(() => refreshWidgets(root), 0);
		window.setTimeout(() => refreshWidgets(root), 120);
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => initWidgets());
	} else {
		initWidgets();
	}

	document.addEventListener("shopify:section:load", (event) => {
		initWidgets(event.target);
		scheduleRefresh(event.target);
	});

	document.addEventListener("variant:change", () => {
		scheduleRefresh();
	});

	document.addEventListener("change", (event) => {
		const target = event.target;
		if (!(target instanceof Element)) return;
		if (!target.matches(VARIANT_INPUT_SELECTOR)) return;
		const productSection = target.closest(".product-section");
		scheduleRefresh(productSection || document);
	});
})();
/* ===== BSTRO Feature: Custom Subscription Widget (END) ===== */
